import type {
  AgeingDetailRow,
  AuditApprovalEvent,
  FixedAssetRegisterReport,
  GrantSummaryReport,
  OutstandingFeesReport,
  PayablesControlAccountReconciliation,
  ReceivablesControlAccountReconciliation,
} from "../../services/financialService";
import { byNumber, byText, humanise, reversed, type RegisterConfig, type RegisterView } from "../register/registerLogic";

/**
 * Views, filters and sorts for the lists on the Management Reports page. The
 * lists arrive whole from the report endpoints, so they filter in the browser.
 */

export type StudentOwing = OutstandingFeesReport["by_student"][number];
export type ControlStudent = ReceivablesControlAccountReconciliation["students"][number];
export type ControlSupplier = PayablesControlAccountReconciliation["suppliers"][number];
export type GrantSummaryRow = GrantSummaryReport["detail"][number];
export type FixedAssetRow = FixedAssetRegisterReport["detail"][number];

/** The page's sections, in the order of the switch at the top. */
export const REPORT_VIEWS = [
  { key: "income", label: "Income & cash" },
  { key: "arrears", label: "Arrears" },
  { key: "ageing", label: "Ageing" },
  { key: "reconciliations", label: "Reconciliations" },
  { key: "grants", label: "Grants & assets" },
  { key: "audit", label: "Audit trail" },
] as const;
export type ReportView = (typeof REPORT_VIEWS)[number]["key"];

export const isReportView = (value: string | null): value is ReportView =>
  REPORT_VIEWS.some((view) => view.key === value);

// Ageing -----------------------------------------------------------------------------

export const AGEING_BUCKETS = [
  { key: "current", label: "Not yet due", dot: "bg-emerald-500" },
  { key: "1_30", label: "1–30 days", dot: "bg-amber-400" },
  { key: "31_60", label: "31–60 days", dot: "bg-amber-500" },
  { key: "61_90", label: "61–90 days", dot: "bg-orange-500" },
  { key: "over_90", label: "Over 90 days", dot: "bg-red-500" },
] as const;

export const ageingBucket = (key: string) =>
  AGEING_BUCKETS.find((bucket) => bucket.key === key) ?? { key, label: humanise(key), dot: "bg-gray-400" };

const ageingViews: RegisterView<AgeingDetailRow>[] = [
  { key: "all", label: "All" },
  { key: "overdue", label: "Overdue", match: (row) => row.bucket !== "current" },
  ...AGEING_BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, match: (row: AgeingDetailRow) => row.bucket === bucket.key })),
];

const ageingSorts = (party: string): RegisterConfig<AgeingDetailRow>["sorts"] => [
  { key: "-days", label: "Most overdue first", compare: reversed(byNumber<AgeingDetailRow>((row) => row.days_overdue)) },
  { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<AgeingDetailRow>((row) => row.balance)) },
  { key: "due", label: "Due date (earliest first)", compare: byText<AgeingDetailRow>((row) => row.due_date) },
  { key: "name", label: `${party} (A–Z)`, compare: byText<AgeingDetailRow>((row) => row.student_name) },
];

export const receivablesAgeingConfig: RegisterConfig<AgeingDetailRow> = {
  search: (row) => [row.student_name, row.student_code, row.invoice_number, row.class_name].join(" "),
  views: ageingViews,
  date: { label: "Due", value: (row) => row.due_date },
  category: { label: "Class", value: (row) => row.class_name },
  sorts: ageingSorts("Student"),
};

export const payablesAgeingConfig: RegisterConfig<AgeingDetailRow> = {
  search: (row) => [row.student_name, row.invoice_number].join(" "),
  views: ageingViews,
  date: { label: "Due", value: (row) => row.due_date },
  sorts: ageingSorts("Supplier"),
};

// Arrears ----------------------------------------------------------------------------

export const studentsOwingConfig: RegisterConfig<StudentOwing> = {
  search: (row) => [row.student_name, row.student_code, row.class_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "several", label: "Two or more invoices", match: (row) => row.invoice_count > 1 },
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "-balance", label: "Amount owed (highest first)", compare: reversed(byNumber<StudentOwing>((row) => row.balance)) },
    { key: "balance", label: "Amount owed (lowest first)", compare: byNumber<StudentOwing>((row) => row.balance) },
    { key: "name", label: "Name (A–Z)", compare: byText<StudentOwing>((row) => row.student_name) },
    { key: "class", label: "Class", compare: (a, b) => a.class_name.localeCompare(b.class_name, undefined, { numeric: true }) || a.student_name.localeCompare(b.student_name) },
  ],
};

/** Each class's share of what is owed, largest first, for the bars. */
export function classShares(byClass: OutstandingFeesReport["by_class"]) {
  const total = byClass.reduce((sum, row) => sum + Number(row.amount), 0);
  return [...byClass]
    .sort((a, b) => Number(b.amount) - Number(a.amount))
    .map((row) => ({ ...row, share: total ? (Number(row.amount) / total) * 100 : 0 }));
}

// Reconciliations --------------------------------------------------------------------

const balanceViews = <T extends { balance: string }>(owing: string): RegisterView<T>[] => [
  { key: "all", label: "All" },
  { key: "owing", label: owing, match: (row) => Number(row.balance) > 0 },
  { key: "credit", label: "In credit", match: (row) => Number(row.balance) < 0 },
  { key: "settled", label: "Settled", match: (row) => Number(row.balance) === 0 },
];

export const controlStudentConfig: RegisterConfig<ControlStudent> = {
  search: (row) => row.student_name,
  views: balanceViews<ControlStudent>("Owing"),
  sorts: [
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<ControlStudent>((row) => row.balance)) },
    { key: "name", label: "Name (A–Z)", compare: byText<ControlStudent>((row) => row.student_name) },
  ],
};

export const controlSupplierConfig: RegisterConfig<ControlSupplier> = {
  search: (row) => row.supplier_name,
  views: balanceViews<ControlSupplier>("Owed"),
  sorts: [
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<ControlSupplier>((row) => row.balance)) },
    { key: "name", label: "Name (A–Z)", compare: byText<ControlSupplier>((row) => row.supplier_name) },
  ],
};

// Grants and assets ------------------------------------------------------------------

export const grantSummaryConfig: RegisterConfig<GrantSummaryRow> = {
  search: (row) => [row.name, row.donor, row.project].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.status === "active" },
    { key: "closed", label: "Closed", match: (row) => row.status === "closed" },
  ],
  date: { label: "Funding starts", value: (row) => row.funding_period_start },
  sorts: [
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<GrantSummaryRow>((row) => row.balance)) },
    { key: "-start", label: "Newest first", compare: reversed(byText<GrantSummaryRow>((row) => row.funding_period_start)) },
    { key: "donor", label: "Donor (A–Z)", compare: byText<GrantSummaryRow>((row) => row.donor) },
  ],
};

const OUT_OF_USE = ["disposed", "lost", "stolen"];

export const fixedAssetConfig: RegisterConfig<FixedAssetRow> = {
  search: (row) => [row.asset_tag, row.name, row.category, row.supplier].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "held", label: "Held", match: (row) => !OUT_OF_USE.includes(row.status) },
    { key: "gone", label: "Disposed or lost", match: (row) => OUT_OF_USE.includes(row.status) },
  ],
  date: { label: "Bought", value: (row) => row.purchase_date },
  category: { label: "Category", value: (row) => row.category },
  sorts: [
    { key: "tag", label: "Asset tag", compare: (a, b) => a.asset_tag.localeCompare(b.asset_tag, undefined, { numeric: true }) },
    { key: "-nbv", label: "Book value (highest first)", compare: reversed(byNumber<FixedAssetRow>((row) => row.net_book_value)) },
    { key: "-bought", label: "Newest first", compare: reversed(byText<FixedAssetRow>((row) => row.purchase_date)) },
  ],
};

export const assetStatusDot = (status: string) =>
  status === "active" ? "bg-emerald-500" : status === "maintenance" ? "bg-amber-500" : OUT_OF_USE.includes(status) ? "bg-red-500" : "bg-gray-400";

// Audit trail ------------------------------------------------------------------------

/** The workflow's model names, in the words the finance pages use. */
const DOCUMENT_NAMES: Record<string, string> = {
  account: "Account",
  journalentry: "Journal entry",
  vendorbill: "Supplier bill",
  budget: "Budget",
  creditnote: "Credit note",
  payrollperiod: "Payroll run",
  payrollsettlement: "Payroll payment",
  salaryadvance: "Salary advance",
  salaryadvancedisbursement: "Advance payout",
  refundrequest: "Refund",
  grant: "Grant",
  salarychangerequest: "Salary change",
  accountrole: "Account role",
  bankreconciliationsignoff: "Bank sign-off",
  accountingperiodreopenrequest: "Period reopening",
};

export const documentName = (model: string) => DOCUMENT_NAMES[model] ?? humanise(model);

export const auditEventConfig: RegisterConfig<AuditApprovalEvent> = {
  search: (event) => [event.action_display, event.actor_name, documentName(event.document_type), event.object_id, event.comment].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "submitted", label: "Submitted & edited", match: (event) => ["submitted", "edited"].includes(event.action) },
    { key: "approved", label: "Verified & approved", match: (event) => ["verified", "approved", "posted"].includes(event.action) },
    { key: "rejected", label: "Rejected & returned", match: (event) => ["rejected", "returned"].includes(event.action) },
    { key: "reversed", label: "Reversed & cancelled", match: (event) => ["reversed", "cancelled"].includes(event.action) },
  ],
  date: { label: "Date", value: (event) => event.created_at },
  category: { label: "Document", value: (event) => documentName(event.document_type) },
  sorts: [
    { key: "-at", label: "Newest first", compare: reversed(byText<AuditApprovalEvent>((event) => event.created_at)) },
    { key: "at", label: "Oldest first", compare: byText<AuditApprovalEvent>((event) => event.created_at) },
    { key: "actor", label: "Person (A–Z)", compare: byText<AuditApprovalEvent>((event) => event.actor_name) },
  ],
};
